import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Layers } from 'lucide-react';
import { exerciseAPI, topicAPI } from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { useLevel } from '../context/LevelContext';
import { slugifyLessonTitle } from '../utils/lessonSlug';
import { appLangToUrlLang, buildLevelPath, urlLangToAppLang } from '../utils/routeLanguage';
import '../styles/lesson-preview.css';

const normalizeMCOptions = (exercise) => {
  let options = exercise.content?.options || exercise.options || [];
  options = options.map(o => (typeof o === 'string' ? { text: o } : (o || { text: '' })));
  if (options.length > 0 && options[0].order !== undefined) {
    options = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  return options;
};

// ── Flashcard exercise preview — first pair, flippable ──
const FlashcardPreview = ({ exercise, topic, language }) => {
  const [flipped, setFlipped] = useState(false);
  const pairs = exercise.pairs || exercise.content?.pairs || [];
  const pair = pairs[0];
  if (!pair) return null;

  const flipHint = language === 'uk' ? '↻ натисни щоб перевернути' : '↻ нажми чтобы перевернуть';
  const translationLabel = language === 'uk' ? 'Переклад' : 'Перевод';
  const moreLabel = language === 'uk' ? 'карток' : 'карточек';

  return (
    <div className="pfc-area">
      <div
        className="pfc-wrapper"
        onClick={(e) => { e.stopPropagation(); setFlipped(f => !f); }}
      >
        <div className={`pfc-inner${flipped ? ' flipped' : ''}`}>
          {/* Front */}
          <div className="pfc-face">
            <div className="fc-badge" style={{ fontSize: 11, padding: '4px 12px', marginBottom: 16, alignSelf: 'center' }}>
              {topic?.level} · {topic?.germanTitle || topic?.title}
            </div>
            <div className="pfc-word">{pair.german}</div>
            {pair.germanDescription && (
              <div className="fc-grammar" style={{ fontSize: 13, marginTop: 6 }}>{pair.germanDescription}</div>
            )}
            <div className="pfc-tap-hint">{flipHint}</div>
          </div>
          {/* Back */}
          <div className="pfc-face pfc-back">
            <div className="pfc-back-label">{translationLabel}</div>
            <div className="pfc-word pfc-translation">{pair.english || pair.russian}</div>
            <div className="fc-divider" style={{ margin: '8px auto', width: 40 }} />
            <div className="pfc-word" style={{ fontSize: 'clamp(14px, 2vw, 18px)', color: 'rgba(245,244,240,0.45)' }}>{pair.german}</div>
          </div>
        </div>
      </div>
      {pairs.length > 1 && (
        <p className="pfc-count">+ {pairs.length - 1} {moreLabel}</p>
      )}
    </div>
  );
};

// ── MC / ImageChoice exercise preview ──
const MCPreview = ({ exercise }) => {
  const question = exercise.question || exercise.content?.question || '';
  const options = normalizeMCOptions(exercise);

  return (
    <div className="preview-mc-body">
      {question && <div className="preview-mc-question">{question}</div>}
      <div className="mc-options" style={{ maxWidth: '100%' }}>
        {options.map((opt, i) => (
          <div
            key={i}
            className="mc-option"
            style={{ fontSize: 14, padding: '12px 16px', cursor: 'default' }}
          >
            {opt.text}
          </div>
        ))}
      </div>
    </div>
  );
};

export const LessonPreviewPage = () => {
  const { lang, level, topicSlug } = useParams();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { selectedLevel } = useLevel();
  const [topic, setTopic] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  const levelPath = buildLevelPath(language, selectedLevel);

  useEffect(() => {
    const nextLanguage = urlLangToAppLang(lang);
    if ((nextLanguage === 'ru' || nextLanguage === 'uk') && nextLanguage !== language) {
      setLanguage(nextLanguage);
    }
  }, [lang, language, setLanguage]);

  useEffect(() => {
    const fetchLessonPreview = async () => {
      try {
        const requestLanguage = urlLangToAppLang(lang);
        const topicsResponse = await topicAPI.getAllTopics({ language: requestLanguage, includeExercises: false });
        const topics = Array.isArray(topicsResponse.data) ? topicsResponse.data : [];

        const matchedTopic = topics.find((item) => {
          const itemLevel = String(item.level || '').toLowerCase();
          const itemSlug = slugifyLessonTitle(item.germanTitle || item.title || '');
          return itemLevel === String(level || '').toLowerCase() && itemSlug === topicSlug;
        });

        if (!matchedTopic) {
          setTopic(null);
          setExercises([]);
          return;
        }

        setTopic(matchedTopic);

        const exerciseResponse = await exerciseAPI.getExercises(matchedTopic.id);
        const exerciseList = Array.isArray(exerciseResponse.data) ? exerciseResponse.data : [];
        const sorted = [...exerciseList].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
        setExercises(sorted);
      } catch (error) {
        console.error('Error loading lesson preview:', error);
        setTopic(null);
        setExercises([]);
      } finally {
        setLoading(false);
      }
    };

    if (level && topicSlug) {
      fetchLessonPreview();
    }
  }, [lang, level, topicSlug]);

  const startLesson = () => {
    if (!topic) {
      return;
    }
    navigate(`/${appLangToUrlLang(language)}/${topic.level}/${topicSlug}/lesson`);
  };

  if (loading) {
    return (
      <div className="exercise-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(245,244,240,0.5)', fontSize: 18 }}>{t('loading')}</p>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="exercise-page" style={{ padding: 32 }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <button onClick={() => navigate(levelPath)} className="ticket-back-btn" style={{ marginBottom: 16 }}>
            <ArrowLeft size={16} />
            <span>{t('back')}</span>
          </button>
          <div className="results-tip">
            <p>{t('topicsNotFound')}</p>
          </div>
        </div>
      </div>
    );
  }

  const pageTitle = language === 'uk' ? 'Передперегляд уроку' : 'Предпросмотр урока';
  const allExercisesLabel = language === 'uk' ? 'Усі вправи' : 'Все упражнения';
  const startLabel = language === 'uk' ? 'Почати урок' : 'Начать урок';

  return (
    <div className="exercise-page lesson-preview-page" style={{ padding: '32px 20px 48px' }}>
      <div className="lesson-preview-shell">
        <button onClick={() => navigate(levelPath)} className="ticket-back-btn" style={{ marginBottom: 16 }}>
          <ArrowLeft size={16} />
          <span>{t('back')}</span>
        </button>

        <div className="lesson-preview-header">
          <div>
            <p className="lesson-preview-kicker">{pageTitle}</p>
            <h1 className="lesson-preview-title">{topic.germanTitle || topic.title}</h1>
            <p className="lesson-preview-subtitle">{topic.germanDescription || topic.description || ''}</p>
          </div>
          <button onClick={startLesson} className="modern-button-primary lesson-preview-start-btn">
            <Play size={18} />
            {startLabel}
          </button>
        </div>

        <div className="lesson-preview-stats">
          <div className="lesson-preview-stat-card">
            <Layers size={18} />
            <span>{t('totalExercises')}</span>
            <strong>{exercises.length}</strong>
          </div>
        </div>

        <h2 className="lesson-preview-list-title">{allExercisesLabel}</h2>

        {exercises.length === 0 ? (
          <div className="results-tip">
            <p>{t('noExercises')}</p>
          </div>
        ) : (
          <div className="lesson-preview-grid">
            {exercises.map((item, index) => {
              return (
                <div
                  key={item.id || `${item.type}-${index}`}
                  className="preview-exercise-card"
                >
                  <div className="preview-card-body">
                    {item.type === 'flashcard' && (
                      <FlashcardPreview exercise={item} topic={topic} language={language} />
                    )}
                    {(item.type === 'multipleChoice' || item.type === 'imageChoice') && (
                      <MCPreview exercise={item} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
